import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';

import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CallCenterStatsService } from '../../../services/call-center-stats.service';
import { SalesStatsService } from '../../../services/sales-stats.service';
import { StatsAggregationService } from '../../../services/stats-aggregation.service';
import { CallCenterStatsData } from '../../../interfaces/call-center-stats.interface';
import { SalesStats } from '../../../interfaces/sales-stats.interface';
import { YearlyMetricSummary } from '../../../interfaces/statistics.interface';
import { User } from '../../../interfaces/auth.interface';

/**
 * Éves összesítő metrika interfész - havi bontású adatokkal
 */
export interface YearlyMetric {
  /** Metrika neve (pl. 'Indított hívás') */
  name: string;
  /** Havi plan összegek (12 hónap) */
  planValues: number[];
  /** Havi actual összegek (12 hónap) */
  actualValues: number[];
  /** Havi százalékok (12 hónap) */
  percentageValues: number[];
}

/**
 * Oszlop definíció az éves táblázathoz
 */
export interface YearlyColumn {
  /** Oszlop fejléc (pl. 'JAN', 'JAN-ÁPR') */
  label: string;
  /** Típus: hónap, negyedéves összesítő, vagy éves összesítő */
  type: 'month' | 'quarter-summary' | 'yearly-summary';
  /** Hónap indexek amiket összegez (0-alapú) */
  monthIndices: number[];
}

@Component({
  selector: 'app-yearly-statistics-table',
  standalone: true,
  imports: [MatSnackBarModule],
  templateUrl: './yearly-statistics-table.component.html',
  styleUrls: ['./yearly-statistics-table.component.scss']
})
export class YearlyStatisticsTableComponent implements OnInit, OnChanges {
  @Input() year: number = 2026;
  @Input() colleagueName: string = '';
  @Input() userId: number = 0;
  @Input() isKumMode: boolean = false;
  @Input() isSummaryTable: boolean = false;
  @Input() allColleaguesData: { colleague: User, userId: number }[] = [];

  metrics: YearlyMetric[] = [];
  columns: YearlyColumn[] = [];
  isLoading: boolean = false;
  private initialized: boolean = false;

  private readonly MONTH_LABELS = ['JAN', 'FEB', 'MÁR', 'ÁPR', 'MÁJ', 'JÚN', 'JÚL', 'AUG', 'SZEP', 'OKT', 'NOV', 'DEC'];
  private readonly QUARTER_LABELS = ['JAN-ÁPR', 'MÁJ-AUG', 'SZEP-DEC'];

  constructor(
    private cdr: ChangeDetectorRef,
    private callCenterStatsService: CallCenterStatsService,
    private salesStatsService: SalesStatsService,
    private statsAggregationService: StatsAggregationService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.buildColumns();
    if (!this.initialized) {
      this.initialized = true;
      this.initializeMetrics();
      this.loadYearlyData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return; // ngOnInit fogja kezelni az első betöltést
    if (changes['year'] || changes['userId'] || changes['isKumMode'] || changes['allColleaguesData']) {
      this.initializeMetrics();
      this.loadYearlyData();
    }
  }

  /**
   * Oszlopok felépítése: 12 hónap + 3 negyedéves összesítő + 1 éves összesítő
   */
  private buildColumns(): void {
    this.columns = [];

    for (let q = 0; q < 3; q++) {
      // 4 hónap
      for (let m = 0; m < 4; m++) {
        const monthIndex = q * 4 + m;
        this.columns.push({
          label: this.MONTH_LABELS[monthIndex],
          type: 'month',
          monthIndices: [monthIndex]
        });
      }
      // Negyedéves összesítő
      const quarterStart = q * 4;
      this.columns.push({
        label: this.QUARTER_LABELS[q],
        type: 'quarter-summary',
        monthIndices: [quarterStart, quarterStart + 1, quarterStart + 2, quarterStart + 3]
      });
    }

    // Éves összesítő
    this.columns.push({
      label: 'Összesen / Átlag',
      type: 'yearly-summary',
      monthIndices: Array.from({ length: 12 }, (_, i) => i)
    });
  }

  /**
   * Alapértelmezett metrikák inicializálása
   */
  private initializeMetrics(): void {
    if (this.isKumMode) {
      this.metrics = [
        this.createEmptyMetric('Egyeztetett időpont'),
        this.createEmptyMetric('Sikeres első tárgyalás'),
        this.createEmptyMetric('Sikeres felmérés'),
        this.createEmptyMetric('Sikeres ajánlat prezentáció'),
        this.createEmptyMetric('Sikeres zárás')
      ];
    } else {
      this.metrics = [
        this.createEmptyMetric('Indított hívás'),
        this.createEmptyMetric('Egyeztetett időpont'),
        this.createEmptyMetric('Vagyonra egyeztetett')
      ];
    }
  }

  private createEmptyMetric(name: string): YearlyMetric {
    return {
      name,
      planValues: new Array(12).fill(0),
      actualValues: new Array(12).fill(0),
      percentageValues: new Array(12).fill(0)
    };
  }

  /**
   * Éves adat betöltése — DB aggregáció egyetlen kéréssel (régi: 12 API hívás)
   */
  private loadYearlyData(): void {
    if (this.isSummaryTable) {
      this.loadSummaryYearlyData();
      return;
    }

    if (!this.userId || this.userId === 0) return;

    this.isLoading = true;
    this.initializeMetrics();

    const observable = this.isKumMode
      ? this.statsAggregationService.getKumYearlyUserSummary(this.userId, this.year)
      : this.statsAggregationService.getCCYearlyUserSummary(this.userId, this.year);

    observable.pipe(
      catchError((error) => {
        console.error('DB éves összesítő hiba, fallback régi logikára:', error);
        // Fallback: régi 12×forkJoin módszer
        this.loadYearlyDataLegacy();
        return of(null);
      })
    ).subscribe({
      next: (dbMetrics: YearlyMetricSummary[] | null) => {
        if (!dbMetrics) return; // fallback kezeli

        this.applyYearlyDbMetrics(dbMetrics);
        this.calculatePercentages();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * DB éves metrikák alkalmazása a belső metrics struktúrára
   */
  private applyYearlyDbMetrics(dbMetrics: YearlyMetricSummary[]): void {
    const metricNameMap = this.isKumMode
      ? {
          'appointment_scheduled': 'Egyeztetett időpont',
          'successful_first_meetings': 'Sikeres első tárgyalás',
          'successful_assessments': 'Sikeres felmérés',
          'successful_proposals': 'Sikeres ajánlat prezentáció',
          'successful_closures': 'Sikeres zárás'
        }
      : {
          'initiated_calls': 'Indított hívás',
          'appointment_scheduled': 'Egyeztetett időpont',
          'property_insurance': 'Vagyonra egyeztetett'
        };

    for (const dbRow of dbMetrics) {
      const displayName = metricNameMap[dbRow.metric_name as keyof typeof metricNameMap];
      if (!displayName) continue;

      const metric = this.metrics.find(m => m.name === displayName);
      if (!metric) continue;

      const monthIndex = dbRow.month_number - 1; // 1-based → 0-based
      metric.planValues[monthIndex] = dbRow.plan_total;
      metric.actualValues[monthIndex] = dbRow.actual_total;
    }
  }

  /**
   * Régi éves betöltés — fallback, ha a DB függvény nem elérhető
   */
  private loadYearlyDataLegacy(): void {
    if (!this.userId || this.userId === 0) return;

    this.isLoading = true;
    this.initializeMetrics();

    const monthRequests = [];
    for (let month = 1; month <= 12; month++) {
      if (this.isKumMode) {
        monthRequests.push(
          this.salesStatsService.getMonthStats(this.userId, month, this.year).pipe(
            catchError(() => of([]))
          )
        );
      } else {
        monthRequests.push(
          this.callCenterStatsService.getMonthStats(this.userId, month, this.year).pipe(
            catchError(() => of([]))
          )
        );
      }
    }

    forkJoin(monthRequests).subscribe({
      next: (allMonthsData) => {
        allMonthsData.forEach((weeklyStats: any[], monthIndex: number) => {
          if (this.isKumMode) {
            this.aggregateSalesMonthData(weeklyStats as SalesStats[], monthIndex);
          } else {
            this.aggregateCCMonthData(weeklyStats as CallCenterStatsData[], monthIndex);
          }
        });

        this.calculatePercentages();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.snackBar.open(
          `Hiba az éves adatok betöltésekor: ${error.message || 'Ismeretlen hiba'}`,
          'Bezárás',
          { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] }
        );
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Összesítő táblázat — DB aggregáció egyetlen kéréssel (régi: N kolléga × 12 hónap)
   */
  private loadSummaryYearlyData(): void {
    if (!this.allColleaguesData || this.allColleaguesData.length === 0) return;

    this.isLoading = true;
    this.initializeMetrics();

    const observable = this.isKumMode
      ? this.statsAggregationService.getKumYearlyAllSummary(this.year)
      : this.statsAggregationService.getCCYearlyAllSummary(this.year);

    observable.pipe(
      catchError((error) => {
        console.error('DB éves összesítő hiba, fallback régi logikára:', error);
        this.loadSummaryYearlyDataLegacy();
        return of(null);
      })
    ).subscribe({
      next: (dbMetrics: YearlyMetricSummary[] | null) => {
        if (!dbMetrics) return; // fallback kezeli

        this.applyYearlyDbMetrics(dbMetrics);
        this.calculatePercentages();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Régi összesítő betöltés — fallback, ha a DB függvény nem elérhető
   */
  private loadSummaryYearlyDataLegacy(): void {
    if (!this.allColleaguesData || this.allColleaguesData.length === 0) return;

    this.isLoading = true;
    this.initializeMetrics();

    const allRequests: { userId: number, month: number }[] = [];
    const observables: any[] = [];

    this.allColleaguesData.forEach(({ userId }) => {
      for (let month = 1; month <= 12; month++) {
        allRequests.push({ userId, month });
        if (this.isKumMode) {
          observables.push(
            this.salesStatsService.getMonthStats(userId, month, this.year).pipe(
              catchError(() => of([]))
            )
          );
        } else {
          observables.push(
            this.callCenterStatsService.getMonthStats(userId, month, this.year).pipe(
              catchError(() => of([]))
            )
          );
        }
      }
    });

    forkJoin(observables).subscribe({
      next: (allResults: any[]) => {
        allResults.forEach((weeklyStats: any[], index: number) => {
          const { month } = allRequests[index];
          const monthIndex = month - 1;
          if (this.isKumMode) {
            this.aggregateSalesMonthData(weeklyStats as SalesStats[], monthIndex);
          } else {
            this.aggregateCCMonthData(weeklyStats as CallCenterStatsData[], monthIndex);
          }
        });

        this.calculatePercentages();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Call Center havi adatok aggregálása - nap szintű bontással
   */
  private aggregateCCMonthData(weeklyStats: CallCenterStatsData[], monthIndex: number): void {
    if (!weeklyStats || weeklyStats.length === 0) return;

    const month = monthIndex + 1; // 0-based -> 1-based

    const initiatedCallsMetric = this.metrics.find(m => m.name === 'Indított hívás');
    const appointmentMetric = this.metrics.find(m => m.name === 'Egyeztetett időpont');
    const propertyMetric = this.metrics.find(m => m.name === 'Vagyonra egyeztetett');

    weeklyStats.forEach((week: CallCenterStatsData) => {
      if (initiatedCallsMetric) {
        initiatedCallsMetric.planValues[monthIndex] += this.sumArrayPartialForMonth(week.initiated_calls_plan, week.week_number, month, this.year);
        initiatedCallsMetric.actualValues[monthIndex] += this.sumArrayPartialForMonth(week.initiated_calls_actual, week.week_number, month, this.year);
      }
      if (appointmentMetric) {
        appointmentMetric.planValues[monthIndex] += this.sumArrayPartialForMonth(week.appointment_scheduled_plan || [], week.week_number, month, this.year);
        appointmentMetric.actualValues[monthIndex] += this.sumArrayPartialForMonth(week.appointment_scheduled_actual || [], week.week_number, month, this.year);
      }
      if (propertyMetric) {
        propertyMetric.planValues[monthIndex] += this.sumArrayPartialForMonth(week.property_insurance_appointment_plan || [], week.week_number, month, this.year);
        propertyMetric.actualValues[monthIndex] += this.sumArrayPartialForMonth(week.property_insurance_appointment_actual || [], week.week_number, month, this.year);
      }
    });
  }

  /**
   * Sales (KÜM) havi adatok aggregálása - nap szintű bontással
   */
  private aggregateSalesMonthData(weeklyStats: SalesStats[], monthIndex: number): void {
    if (!weeklyStats || weeklyStats.length === 0) return;

    const month = monthIndex + 1; // 0-based -> 1-based

    const metricMap: { [key: string]: { planField: string, actualField: string } } = {
      'Egyeztetett időpont': { planField: 'appointment_scheduled_plan', actualField: 'appointment_scheduled_actual' },
      'Sikeres első tárgyalás': { planField: 'succesfulfirstmeetings_plan', actualField: 'succesfulfirstmeetings_actual' },
      'Sikeres felmérés': { planField: 'successfulassessments_plan', actualField: 'successfulassessments_actual' },
      'Sikeres ajánlat prezentáció': { planField: 'successfulproposalpresentations_plan', actualField: 'successfulproposalpresentations_actual' },
      'Sikeres zárás': { planField: 'successfulclosures_plan', actualField: 'successfulclosures_actual' }
    };

    this.metrics.forEach(metric => {
      const mapping = metricMap[metric.name];
      if (!mapping) return;

      weeklyStats.forEach((week: any) => {
        metric.planValues[monthIndex] += this.sumArrayPartialForMonth(week[mapping.planField] || [], week.week_number, month, this.year);
        metric.actualValues[monthIndex] += this.sumArrayPartialForMonth(week[mapping.actualField] || [], week.week_number, month, this.year);
      });
    });
  }

  /**
   * Százalékok kiszámítása minden metrikához
   */
  private calculatePercentages(): void {
    this.metrics.forEach(metric => {
      for (let i = 0; i < 12; i++) {
        if (metric.planValues[i] > 0) {
          metric.percentageValues[i] = Math.round((metric.actualValues[i] / metric.planValues[i]) * 100);
        } else {
          metric.percentageValues[i] = 0;
        }
      }
    });
  }

  /**
   * Oszlop értékének lekérése adott metrikához és sortípushoz
   */
  getColumnValue(metric: YearlyMetric, column: YearlyColumn, rowType: 'plan' | 'actual'): number {
    const values = rowType === 'plan' ? metric.planValues : metric.actualValues;
    return column.monthIndices.reduce((sum, idx) => sum + (values[idx] || 0), 0);
  }

  /**
   * Oszlop százalékának lekérése
   */
  getColumnPercentage(metric: YearlyMetric, column: YearlyColumn): string {
    const planSum = column.monthIndices.reduce((sum, idx) => sum + (metric.planValues[idx] || 0), 0);
    const actualSum = column.monthIndices.reduce((sum, idx) => sum + (metric.actualValues[idx] || 0), 0);

    if (planSum === 0) return '0%';
    return Math.round((actualSum / planSum) * 100) + '%';
  }

  /**
   * Tömb elemeinek összege
   */
  private sumArray(arr: number[]): number {
    if (!arr || !Array.isArray(arr)) return 0;
    return arr.reduce((sum, val) => sum + (val || 0), 0);
  }

  /**
   * Tömb részleges összege: csak az adott hónapba eső napok értékeit összegzi.
   * A tömb 5 elemű (Hétfő-Péntek), és ha egy hét két hónapot ível át,
   * csak azok a napok számítanak, amelyek ténylegesen az adott hónapba esnek.
   */
  private sumArrayPartialForMonth(arr: number[], weekNumber: number, month: number, year: number): number {
    if (!arr || !Array.isArray(arr)) return 0;

    // ISO hétfő kiszámítása: Jan 4 mindig az 1. ISO hétbe esik
    const jan4 = new Date(year, 0, 4); // Jan 4
    const jan4DayOfWeek = jan4.getDay(); // 0=Sun, 1=Mon, ...
    const jan4IsoDow = jan4DayOfWeek === 0 ? 7 : jan4DayOfWeek;
    // Hét 1 hétfője
    const week1Monday = new Date(jan4.getTime());
    week1Monday.setDate(jan4.getDate() - (jan4IsoDow - 1));
    // Az adott hét hétfője
    const weekMonday = new Date(week1Monday.getTime());
    weekMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

    let total = 0;
    // Hétfőtől péntekig (index 0-4)
    for (let i = 0; i < 5; i++) {
      const day = new Date(weekMonday.getTime());
      day.setDate(weekMonday.getDate() + i);
      // Ha ez a nap az adott hónapba esik (month 1-based, getMonth() 0-based)
      if (day.getMonth() + 1 === month) {
        total += (arr[i] || 0);
      }
    }

    return total;
  }

  /**
   * Oszlop CSS class meghatározása
   */
  getColumnClass(column: YearlyColumn): string {
    switch (column.type) {
      case 'quarter-summary': return 'quarter-summary-col';
      case 'yearly-summary': return 'yearly-summary-col';
      default: return 'month-col';
    }
  }

  /**
   * Fejléc osztály
   */
  getHeaderClass(column: YearlyColumn): string {
    switch (column.type) {
      case 'quarter-summary': return 'quarter-summary-header';
      case 'yearly-summary': return 'yearly-summary-header';
      default: return 'month-header';
    }
  }
}
